import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  getAllUsers, 
  initiatePasswordReset,
  deactivateUser
} from '../../api/admin';
import { User } from '../../types/user';
import Card from '../../components/Card';
import Button from '../../components/Button';
import EmptyStateCard from '../../components/EmptyStateCard';
import RoleAssignmentModal from '../../components/RoleAssignmentModal';
import TenantTeamPanel from '../../components/admin/TenantTeamPanel';

/**
 * T123-T124: User Management Page
 * 
 * Admin-only page for managing user accounts, including:
 * - View all users with status and role badges
 * - Initiate password resets
 * - Approve/reject registrations
 * - Assign roles and deactivate accounts
 */
const UserManagementPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [resetLoading, setResetLoading] = useState<Record<string, boolean>>({});
  const [resetSuccess, setResetSuccess] = useState<Record<string, string>>({});
  const [resetError, setResetError] = useState<Record<string, string>>({});
  const [deactivateLoading, setDeactivateLoading] = useState<Record<string, boolean>>({});
  const [showRoleModal, setShowRoleModal] = useState<string | null>(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAllUsers();
      setUsers(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load users';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  /**
   * T124: Handle password reset initiation
   * Shows loading state, calls API, displays success/error message
   */
  const handlePasswordReset = async (userId: string, userEmail: string) => {
    try {
      // Clear previous messages
      setResetSuccess(prev => ({ ...prev, [userId]: '' }));
      setResetError(prev => ({ ...prev, [userId]: '' }));
      
      // Set loading state
      setResetLoading(prev => ({ ...prev, [userId]: true }));

      // Call API
      await initiatePasswordReset(userId);
      
      // Show success message
      setResetSuccess(prev => ({
        ...prev,
        [userId]: `Password reset email sent to ${userEmail}`
      }));

      // Clear success message after 5 seconds
      setTimeout(() => {
        setResetSuccess(prev => ({ ...prev, [userId]: '' }));
      }, 5000);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send password reset email';
      setResetError(prev => ({
        ...prev,
        [userId]: errorMessage
      }));

      // Clear error message after 5 seconds
      setTimeout(() => {
        setResetError(prev => ({ ...prev, [userId]: '' }));
      }, 5000);
    } finally {
      setResetLoading(prev => ({ ...prev, [userId]: false }));
    }
  };

  /**
   * T162: Handle user deactivation/reactivation
   * Shows confirmation, calls API, updates user list
   */
  const handleDeactivateToggle = async (userId: string, currentStatus: string) => {
    const action = currentStatus === 'active' ? 'deactivate' : 'reactivate';
    
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) {
      return;
    }

    try {
      setDeactivateLoading(prev => ({ ...prev, [userId]: true }));
      
      await deactivateUser(userId);
      
      // Refresh user list
      await loadUsers();
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : `Failed to ${action} user`;
      alert(errorMessage);
    } finally {
      setDeactivateLoading(prev => ({ ...prev, [userId]: false }));
    }
  };

  const getStatusBadgeClass = (status: string): string => {
    switch (status) {
      case 'active':
        return 'bg-success/15 text-success';
      case 'pending':
        return 'bg-warning/15 text-warning';
      case 'suspended':
        return 'bg-error/15 text-error';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const getRoleBadgeClass = (role: string): string => {
    switch (role) {
      case 'Admin':
        return 'bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200';
      case 'Portfolio Manager':
        return 'bg-accent-100 text-accent-800 dark:bg-accent-900 dark:text-accent-200';
      case 'Member':
        return 'bg-secondary-100 text-secondary-800 dark:bg-secondary-900 dark:text-secondary-200';
      case 'Viewer':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="loading-container min-h-screen">
        <div className="spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-[1600px] mx-auto px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12 py-8">
        <div className="rounded-lg px-4 py-3 bg-error/10 dark:bg-error/20 border border-error/30 text-error">
          <p className="font-bold">Error</p>
          <p>{error}</p>
          <Button variant="outline" onClick={loadUsers} className="mt-4">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1600px] mx-auto px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12 py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          User Management
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Manage your workspace's team, seats, roles and permissions
        </p>
      </div>

      {/* Multi-tenancy: workspace membership and seats. Sits above the account list so
          "who is in my workspace" and "what can they do" stay together. */}
      <TenantTeamPanel />

      <div className="grid gap-4">
        {users.map(user => (
          <Card key={user.id} variant="default">
            <Card.Body>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                {/* User Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {user.full_name || user.username}
                    </h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(user.status || 'active')}`}>
                      {user.status || 'active'}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleBadgeClass(user.system_role || 'Member')}`}>
                      {user.system_role || 'Member'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    @{user.username}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {user.email}
                  </p>
                  {user.last_login_at && (
                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                      Last login: {new Date(user.last_login_at).toLocaleString()}
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-2 md:items-end">
                  {/* T161: Role Management */}
                  {currentUser?.id !== user.id && (
                    <div className="flex flex-col gap-2">
                      <div className="flex gap-2 items-center">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          System: {user.system_role || 'Member'}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowRoleModal(user.id)}
                        >
                          Assign Roles
                        </Button>
                      </div>
                      
                      {/* Programme Roles */}
                      {user.programme_roles && user.programme_roles.length > 0 && (
                        <div className="text-xs text-gray-500 dark:text-gray-500">
                          <div className="font-medium mb-1">Programme Roles:</div>
                          {user.programme_roles.map((role, index) => (
                            <div key={index} className="ml-2">
                              {role.role} in programme
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Project Roles */}
                      {user.project_roles && user.project_roles.length > 0 && (
                        <div className="text-xs text-gray-500 dark:text-gray-500">
                          <div className="font-medium mb-1">Project Roles:</div>
                          {user.project_roles.map((role, index) => (
                            <div key={index} className="ml-2">
                              {role.role} in project
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* T162: Deactivate/Reactivate Button */}
                  {currentUser?.id !== user.id && (
                    <Button
                      variant={user.status === 'active' ? 'danger' : 'primary'}
                      size="sm"
                      onClick={() => handleDeactivateToggle(user.id, user.status || 'active')}
                      disabled={deactivateLoading[user.id]}
                    >
                      {deactivateLoading[user.id] 
                        ? '...' 
                        : user.status === 'active' 
                          ? 'Deactivate' 
                          : 'Reactivate'}
                    </Button>
                  )}

                  {/* Reset Password Button - T124 */}
                  {user.status === 'active' && currentUser?.id !== user.id && (
                    <div className="flex flex-col gap-2">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handlePasswordReset(user.id, user.email)}
                        disabled={resetLoading[user.id]}
                      >
                        {resetLoading[user.id] ? 'Sending...' : 'Reset Password'}
                      </Button>
                      
                      {/* Success Message */}
                      {resetSuccess[user.id] && (
                        <p className="text-sm text-success">
                          {resetSuccess[user.id]}
                        </p>
                      )}
                      
                      {/* Error Message */}
                      {resetError[user.id] && (
                        <p className="text-sm text-error">
                          {resetError[user.id]}
                        </p>
                      )}
                    </div>
                  )}

                  {user.id === currentUser?.id && (
                    <p className="text-xs text-gray-500 dark:text-gray-500">
                      (You)
                    </p>
                  )}
                </div>
              </div>
            </Card.Body>
          </Card>
        ))}

        {users.length === 0 && (
          <EmptyStateCard
            icon="👥"
            title="No users found"
            message="There are no user accounts to display."
          />
        )}
      </div>

      {/* Role Assignment Modal */}
      {showRoleModal && (() => {
        const user = users.find(u => u.id === showRoleModal);
        return user ? (
          <RoleAssignmentModal
            userId={user.id}
            userName={user.full_name || user.username}
            currentSystemRole={user.system_role || 'Member'}
            onSuccess={() => {
              // Refresh the user list (e.g. system-role badge) but keep the
              // Manage Access modal open so several changes can be made in one go.
              loadUsers();
            }}
            onClose={() => setShowRoleModal(null)}
          />
        ) : null;
      })()}
    </div>
  );
};

export default UserManagementPage;
